import type { ReactElement } from 'react';
import { cloneElement } from 'react';

import { useTheme } from '@emotion/react';
import { isNil } from 'ramda';

import { useBreakpoint } from '@twistopayments/design-system/hooks';
import type { IconProps } from '@twistopayments/design-system/icons/constants';
import { styleUtils } from '@twistopayments/design-system/styles';

import { Avatar } from '../../atoms/avatar';
import { Typography } from '../../atoms/typography';

import * as styles from './action-content.styles';

type Props = {
  disabled?: boolean;
  // icon size is controlled by Action fill the size prop only in case you wish to override the default responsive behavior
  icon: ReactElement;
  endAdornment?: ReactElement;
  caption: ReactElement | string;
  label?: ReactElement | string;
};

export const ActionContent = ({ disabled = false, icon, caption, label, endAdornment }: Props) => {
  const theme = useTheme();

  const isMobileDevice = useBreakpoint('xsDown');
  const mobileIconSize = isMobileDevice ? '32' : '40';

  const sizeProp = icon.props.size as IconProps['size'];
  const iconSize = isNil(sizeProp) || sizeProp === 'inherit' ? mobileIconSize : sizeProp;

  const Icon = cloneElement<Pick<IconProps, 'size' | 'color'>>(icon, {
    // if icon size is specified the size will be used otherwise the default responsive value is used
    size: iconSize,
    color: disabled ? 'disabled' : icon.props.color,
  });

  return (
    <>
      <Avatar
        noPlaceholder
        size={isMobileDevice ? '32' : '40'}
        styles={{
          root: (theme) => styles.avatarIcon(theme),
          children: styles.icon,
        }}>
        {Icon}
      </Avatar>
      <div css={styleUtils.flex.grow} data-testid="action-content">
        <Typography
          align="start"
          color="textPrimary"
          css={disabled && styles.disabled}
          variant="b2">
          {caption}
        </Typography>
        {label !== undefined && (
          <Typography
            align="start"
            color="textSecondary"
            css={disabled && styles.disabled}
            variant="b4">
            {label}
          </Typography>
        )}
      </div>
      {endAdornment && (
        <styles.styledEndAdornment theme={theme}>{endAdornment}</styles.styledEndAdornment>
      )}
    </>
  );
};
